<template>
  <div id="roleList">
    <div class="atb-flex atb-flex-row atb-gap-4 atb-pt-6 atb-px-4 atb-pb-4">
      <div class="atb-flex-grow">
        <div class="eds-table-wrap atb-w-full atb-h-full">
          <Toolbar
            class-name="atb-bg-white atb-border-t-0 atb-border-x-0 atb-border-b atb-border-solid atb-border-gray-300"
          >
            <template #toolbarSection>
              <ToolbarSection>
                <template #sectionContent>
                  <Button
                    id="roleListRefreshButton"
                    motif="tertiary"
                    :title="$t('refresh')"
                    aria-label="refresh button"
                    @click="refreshRoles"
                  >
                    <template #icon>
                      <span class="material-icons">loop</span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>
              <ToolbarSection>
                <template #sectionContent>
                  <Button
                    id="roleListCreateButton"
                    motif="tertiary"
                    :title="$t('create')"
                    aria-label="create button"
                    @click="openCreateForm"
                  >
                    <template #icon>
                      <span class="material-icons">add_circle_outline</span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>
              <ToolbarSection>
                <template #sectionContent>
                  <Button
                    id="roleListDeleteButton"
                    motif="tertiary"
                    :title="$t('delete')"
                    aria-label="delete button"
                    @click="deleteRole"
                  >
                    <template #icon>
                      <span class="material-icons icon">
                        delete_outline_icon
                      </span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>
            </template>
          </Toolbar>
          <AgGridVue
            ref="roleListRef"
            id="rolesListAllRolesGrid"
            :style="{ height: '500px' }"
            class="ag-theme-eds atb-w-full atb-h-full"
            :row-model-type="'infinite'"
            :column-defs="columnDefs"
            :pagination="true"
            :pagination-page-size="rowsPerPage"
            :suppress-pagination-panel="true"
            :cache-block-size="rowsPerPage"
            :row-selection="'single'"
            @grid-ready="onGridReady"
            :row-multi-select-with-click="true"
            :suppress-cell-selection="true"
            :suppress-row-click-selection="true"
          />
          <Pagination
            v-model="currentPage"
            :page-count="totalPages"
            :total-rows="totalRows"
            :page-size="rowsPerPage"
            @pageClick="changePageAndRefresh"
            @pageSizeChange="changePageSizeAndRefresh"
            class="atb-bg-white"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import ClickableCell from '@/app/shared/components/grid-cells/ClickableCell.vue';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import Toolbar from '@atb-ui/eds-vue/src/components/Toolbar/Toolbar.vue';
  import ToolbarSection from '@atb-ui/eds-vue/src/components/Toolbar/ToolbarSection.vue';
  import Pagination from '@atb-ui/eds-vue/src/components/Pagination.vue';
  import { AgGridVue } from 'ag-grid-vue3/lib/AgGridVue';
  import { ListEvents } from '@/app/shared/types/list-events.interface';
  import { Role } from '@/app/shared/types/models/role.interface';
  import { useI18n } from 'vue-i18n';
  import { useRoleStore } from '../role-store';
  import { storeToRefs } from 'pinia';
  import { Ref, ref } from 'vue';
  import { GridApi, GridReadyEvent, IGetRowsParams } from 'ag-grid-community';
  import { usePagination } from '@/app/shared/hooks/use-pagination';

  interface RoleListEvents extends ListEvents<Role> {}
  const emit = defineEmits<RoleListEvents>();
  const { t } = useI18n();
  const roleStore = useRoleStore();
  const { roleList } = storeToRefs(roleStore);
  const gridApi = ref(null) as Ref<GridApi | null>;

  const {
    currentPage,
    rowsPerPage,
    totalRows,
    totalPages,
    changePage,
    changePageSize,
    calculatePagination,
  } = usePagination(
    roleList.value.numberOfElements,
    roleList.value.totalElements,
    gridApi
  );

  rowsPerPage.value = 50;

  const changePageAndRefresh = (page: number) => {
    changePage(page);
    gridApi.value?.setDatasource(getAllRoles());
  };

  const changePageSizeAndRefresh = (newPageSize: number | string) => {
    changePageSize(newPageSize);
    gridApi.value?.setDatasource(getAllRoles());
  };

  const editRole = async (roleId: number, roleName: string) => {
    emit('edit', {
      id: roleId,
      name: roleName,
      isNew: false,
    });
  };

  const columnDefs = [
    {
      field: 'check',
      headerName: '',
      width: 56,
      suppressSizeToFit: true,
      headerCheckboxSelection: true,
      checkboxSelection: true,
    },
    {
      field: 'name',
      headerName: t('Role Name'),
      suppressMovable: true,
      width: 200,
      minWidth: 200,
      sortable: true,
      cellRendererFramework: ClickableCell,
      cellRendererParams: {
        onClick: editRole,
      },
    },
    {
      field: 'client.clientName',
      headerName: t('Client'),
      suppressMovable: true,
      width: 120,
      minWidth: 120,
      sortable: true,
    },
  ];

  const getAllRoles = () => {
    return {
      getRows: async ({ successCallback, failCallback }: IGetRowsParams) => {
        try {
          gridApi.value?.showLoadingOverlay();

          const pageNumber = currentPage.value > 0 ? currentPage.value - 1 : 0;

          await roleStore.fetchRoles(
            pageNumber,
            rowsPerPage.value,
            'id',
            'asc'
          );

          const roles = roleList.value.content || [];
          const totalElements = roleList.value.totalElements || 0;

          if (roles.length > 0) {
            gridApi.value?.hideOverlay();
          } else {
            gridApi.value?.showNoRowsOverlay();
          }

          calculatePagination(totalElements);

          successCallback(roles, roleList.value.numberOfElements || 0);
        } catch (error) {
          gridApi.value?.showNoRowsOverlay();
          failCallback();
          console.error((error as Error).message);
        }
      },
    };
  };

  const onGridReady = (params: GridReadyEvent) => {
    gridApi.value = params.api;
    gridApi.value?.setDatasource(getAllRoles());
    gridApi.value.sizeColumnsToFit();
  };

  const refreshRoles = (): void => {
    gridApi.value?.purgeInfiniteCache();
  };

  const deleteRole = () => {
    const selectedRows: Role[] | undefined = gridApi.value?.getSelectedRows();
    emit('deleteById', selectedRows ? selectedRows[0].id : '');
  };

  const openCreateForm = () => {
    emit('create');
  };

  defineExpose({
    refreshRoles,
    gridApi,
  });
</script>

<style scoped>
  .role-list {
    padding: 1rem;
  }

  .role-list__content {
    margin-top: 1rem;
  }

  .icon {
    width: 20px;
  }
</style>
